import { faBolt, faCrosshairs, faXmark } from "@fortawesome/free-solid-svg-icons";
import { Icon } from "../../Icon";
import { tipAttrs } from "../../tooltip";
import { type Combatant, type EncounterState, weaponInstanceKey } from "@lancer-vtt/domain";
import { liveBackswingReservations, overwatchReachOf } from "@lancer-vtt/rules";
import type { ActionDraft } from "../combatInspector";
import { AttackAimControls } from "./AttackAimControls";
import { formatWeaponDamage, formatWeaponRange, weaponChips } from "../weaponFormat";
import { classifySkirmishChooser, classifySkirmishCard, isGrantedShotCard, overwatchThreatShortfall } from "./chooserClassify";
import { WeaponCard } from "./WeaponCard";

/**
 * The **inline skirmish chooser** — the brutalist weapon-card chooser + acc/diff ± stepper that the mech / NPC
 * control panels expand **in place** when Skirmish is armed for their unit, rendered inside the panel's
 * `.unit-sheet-armed-wrap` (the same slot the `activate` {@link ArmedActionPanel} uses). It replaces the
 * skirmish branch of {@link ArmedActionPanel} on those surfaces (the generic pilot/object inspector
 * still uses ArmedActionPanel's select-based skirmish branch). Declares a single-weapon attack — pick a
 * weapon **card** (name · damage · tag chips), an alternate firing profile when the weapon has one, and
 * an acc/diff ± stepper — then the canvas consumes the next target-token click (unchanged `aim`/`fireAt`
 * pipeline; this chooser never fires directly). A weapon-less unit (every NPC today — NPC weapons aren't
 * frozen at spawn yet) shows an honest unarmed-strike state. The × collapses it (disarms the aim).
 *
 * **Per-card greying mirrors the server wall, it never re-enforces it** (CLAUDE.md: "UI mirrors the
 * reducer's rules as affordances"). {@link classifySkirmishCard} decides each card's state and
 * imports the engine's own predicates rather than re-deriving them — including the two **ORDNANCE**
 * timing walls (RAW Core p.105): `"ordnance-window"` when the cost-keyed turn-virgin window has
 * closed (`checkOrdnance` → the shared `turnBudgetVirgin`), and `"ordnance-overwatch"` on the
 * Overwatch arm, where an Ordnance weapon is flatly illegal. Both render as a disabled card with a
 * state word in the damage slot + a `title` worded off the server's own rejection text, so the
 * pre-click annotation and the post-click toast agree.
 *
 * **Lock-aware twin of {@link BarrageChooser}** — takes `state` + `onEndAttack` (both also threaded
 * to the sibling BarrageChooser at each call site)
 * and renders {@link classifySkirmishChooser}'s three states: the primary card list (unchanged), the
 * one eligible Aux rider (marked **RIDER (free)**) with an **END SKIRMISH** decline, or the spent empty
 * state. **Read the render-state derivation there, not here** — this component only branches on it.
 */
export function SkirmishChooser({
  combatant: c,
  state,
  aim,
  setAim,
  onEndAttack,
  onFire,
  onDeclineOverwatch,
}: {
  combatant: Combatant;
  state: EncounterState;
  /** The armed draft — guaranteed skirmish-mode + `attackerId === combatant.id` by the caller. */
  aim: Extract<ActionDraft, { mode: "skirmish" }>;
  setAim: (a: ActionDraft | null) => void;
  /** Dispatch `end_attack` — decline the rider / abandon mid-attack (shared shape with Barrage). */
  onEndAttack: () => void;
  /**
   * Fire the armed shot at a target id — the host's `fireAt` (`CombatApp.tsx`). Used only for the
   * `aim.fixedTargetId` FIRE button (the Overwatch move-checkpoint take flow), where the target is
   * fixed by the trigger so no board click is
   * needed. Every ordinary Skirmish/Overwatch arm still fires by the canvas click-to-target gesture.
   */
  onFire: (targetId: string) => void;
  /**
   * The Overwatch take's × is its explicit decline (`decisions/0189` point 4): the host answers the
   * held checkpoint with Decline. Every other disarm of the take (Esc among them) is local and
   * brings back the Take/Decline window.
   */
  onDeclineOverwatch?: () => void;
}) {
  const weapons = c.base.weapons ?? [];
  // Overwatch arms this SAME chooser with `aim.reaction`
  // set — the weapon/profile/accuracy pick is identical, only the title + the fired intent's
  // `reaction` flag differ, so this is the one shared surface for both.
  const isOverwatch = aim.reaction === true;
  const chooserState = classifySkirmishChooser({ combatant: c, state, isOverwatch });
  // The chooser filter is a MIRROR of what the server will actually free, never a wall of its own —
  // it reads the SAME `liveBackswingReservations` helper the row and `armSkirmish` already read,
  // never re-derived. Overwatch never rides a
  // reservation (it's a reaction; the helper itself would gate on the actor's own ACTING turn, but
  // this stays explicit to match `classifySkirmishChooser`'s own `isOverwatch` short-circuit).
  const liveReservation = isOverwatch ? undefined : liveBackswingReservations(c, state.activation.turn)[0];
  const cardWeapons = liveReservation ? weapons.filter((w) => weaponInstanceKey(w) === liveReservation.weapon) : weapons;
  // A **pre-filled fixed target** — set by the Overwatch move-checkpoint take flow (the mover is
  // fixed by the trigger) and by the unit-ai advisor panel (the ranked plan already picked its
  // target). When present, render an
  // explicit FIRE button (below) that resolves straight against it via `onFire`, so the watcher
  // never re-picks on the board the target the checkpoint already fixed. The weapon/profile/accuracy
  // pick above stays live; only the redundant TARGET pick is
  // removed. Absent on every ordinary arm — those still fire by the canvas click-to-target gesture.
  const fixedTargetId = aim.fixedTargetId;
  const fixedTargetName = fixedTargetId !== undefined ? (state.combatants[fixedTargetId]?.name ?? fixedTargetId) : undefined;
  // An Overwatch shot at a fixed target (the move-checkpoint take flow): grey the cards whose THREAT
  // falls short of it and list the reaching ones first (`overwatch-mid-move` 3.2). The same reach
  // read the offer's prompt names, so the cards and the prompt agree.
  const reach = isOverwatch && fixedTargetId !== undefined ? overwatchReachOf(state, { watcherId: c.id, targetId: fixedTargetId }) : undefined;
  // A granted shot's card (Covering Fire, Impending Threat, Moving Target) is gated on Range
  // server-side, not Threat, and may be Ordnance: never greyed for either.
  const classified = cardWeapons.map((w) => {
    const grantedShot = isOverwatch && fixedTargetId !== undefined && isGrantedShotCard({ weapon: w, watcherId: c.id, state, targetId: fixedTargetId });
    return {
      w,
      verdict: classifySkirmishCard({ weapon: w, combatant: c, state, isOverwatch, grantedShot }),
      short: grantedShot ? undefined : overwatchThreatShortfall({ weapon: w, reach }),
    };
  });
  const blocked = (card: (typeof classified)[number]): number => Number(card.verdict !== "armable" || card.short !== undefined);
  // Stable sort, and only on the take flow: an ordinary Skirmish keeps loadout order.
  const cards = reach === undefined ? classified : [...classified].sort((a, b) => blocked(a) - blocked(b));

  return (
    <div className="skirmish-hud" role="group" aria-label={isOverwatch ? "Overwatch" : "Skirmish"}>
      <div className="skirmish-hud-head">
        <span className="skirmish-hud-title">
          <Icon icon={isOverwatch ? faBolt : faCrosshairs} className="icon-lead" />
          {isOverwatch ? "OVERWATCH" : liveReservation ? "SKIRMISH — FREE BACKSWING" : "SKIRMISH"}
        </span>
        <button
          type="button"
          className="skirmish-hud-x"
          onClick={() => (isOverwatch && onDeclineOverwatch ? onDeclineOverwatch() : setAim(null))}
          aria-label={isOverwatch ? "cancel overwatch" : "cancel skirmish"}
          {...tipAttrs({ label: isOverwatch ? "Decline overwatch" : "Cancel skirmish", key: "Esc" })}
        >
          <Icon icon={faXmark} />
        </button>
      </div>

      {weapons.length === 0 ? (
        <div className="skirmish-hud-empty">NO WEAPONS — unarmed strike (1d6 KIN)</div>
      ) : chooserState.kind === "spent" ? (
        <div className="skirmish-hud-empty">SKIRMISH COMPLETE — Quick action spent.</div>
      ) : (
        <>
          {chooserState.kind === "mid-attack-rider" && (
            <div className="skirmish-hud-target">SKIRMISH IN PROGRESS — primary fired · rider or end</div>
          )}

          <div className="skirmish-hud-weapons">
            {chooserState.kind === "mid-attack-rider"
              ? chooserState.riderWeapon
                ? (() => {
                    const w = chooserState.riderWeapon!;
                    const key = weaponInstanceKey(w);
                    const sel = key === aim.weaponId;
                    const chips = weaponChips(w);
                    return (
                      <button
                        key={key}
                        type="button"
                        className={`skirmish-hud-wpn${sel ? " is-sel" : ""}`}
                        aria-pressed={sel}
                        title="Aux rider — fires free, no additional action spent"
                        onClick={() => setAim({ ...aim, weaponId: sel ? "" : key, profileId: "" })}
                      >
                        <span className="skirmish-hud-wpn-top">
                          <span className="skirmish-hud-wpn-name">{w.name}</span>
                          <span className="skirmish-hud-wpn-dmg">{formatWeaponDamage(w.damage)}</span>
                        </span>
                        <span className="skirmish-hud-rider-badge">RIDER (free)</span>
                        {w.range && w.range.length > 0 && (
                          <span className="skirmish-hud-wpn-range">{formatWeaponRange(w.range)}</span>
                        )}
                        {chips.length > 0 && (
                          <span className="skirmish-hud-wpn-tags">
                            {chips.map((t) => (
                              <span key={t} className="skirmish-hud-chip">
                                {t}
                              </span>
                            ))}
                          </span>
                        )}
                        {w.effect && <span className="skirmish-hud-wpn-effect">{w.effect}</span>}
                      </button>
                    );
                  })()
                : <div className="skirmish-hud-empty">no rider available — end the attack</div>
              : cardWeapons.length === 0
                ? <div className="skirmish-hud-empty">reserved weapon unavailable — end the attack</div>
                : cards.map(({ w, verdict, short }) => {
                  const key = weaponInstanceKey(w);
                  const sel = key === aim.weaponId;
                  // Per-weapon usability greying — mirrors BarrageChooser's slot classifier and
                  // ArmedActionPanel's select. Previously the Skirmish cards greyed NOTHING, so a
                  // depleted-Limited / unloaded / discharged weapon looked armable and the engine
                  // toast-rejected the click. The card body itself is the SHARED {@link WeaponCard}:
                  // `PrepareChooser` renders the same one, so the greying and the server-worded
                  // titles can never drift between arming a Skirmish and READYING one — and RAW
                  // gates a prepare-time weapon by these same Ordnance/Loading walls.
                  return (
                    <WeaponCard
                      key={key}
                      weapon={w}
                      combatant={c}
                      state={verdict}
                      {...(short !== undefined ? { outOfThreat: short } : {})}
                      selected={sel}
                      preview={{ ...aim, weaponId: key, profileId: "" }}
                      onToggle={() => setAim({ ...aim, weaponId: sel ? "" : key, profileId: "" })}
                    />
                  );
                })}
          </div>

          <AttackAimControls combatant={c} state={state} aim={aim} setAim={setAim} />

          {chooserState.kind === "mid-attack-rider" && (
            <div className="skirmish-hud-foot">
              <button type="button" className="skirmish-hud-go" onClick={onEndAttack}>
                END SKIRMISH
              </button>
            </div>
          )}
        </>
      )}

      {/* Auto-targeted FIRE (plus) —
          the target is already fixed, so fire straight at it, no board click. Renders whenever a
          `fixedTargetId` is pinned (an Overwatch reaction is never `"spent"`), including the
          weapon-less unarmed-strike case above. **Two sources set `fixedTargetId` today** — the
          Overwatch take flow (`startOverwatchTake`, `CombatApp.tsx`: the checkpoint's mover is fixed
          by the trigger, `aim.reaction === true`) and the unit-ai advisor panel ({@link AdvisorPanel}:
          an armed plan's target is already picked by the ranking, arms without `reaction`) — this
          button is agnostic to which one armed it for rendering, but the **accessible name is
          source-derived** (reusing the same `isOverwatch` this component already computes above): an
          Overwatch shot really is a reaction ("fire overwatch at …"), an advisor-armed shot really
          isn't ("fire at …") — the visible label stays the single "FIRE AT …" text regardless. */}
      {fixedTargetId !== undefined && chooserState.kind !== "spent" && (
        <div className="skirmish-hud-foot">
          <button
            type="button"
            className="skirmish-hud-go"
            aria-label={isOverwatch ? `fire overwatch at ${fixedTargetName}` : `fire at ${fixedTargetName}`}
            onClick={() => onFire(fixedTargetId)}
          >
            FIRE AT {fixedTargetName?.toUpperCase()}
          </button>
        </div>
      )}
    </div>
  );
}
